import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Box,
  Flex,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Input,
  FormControl,
  FormLabel,
  useDisclosure,
  Select,
  RadioGroup,
  HStack,
  Radio,
  Stack,
  Heading,
  Badge,
  Container,
  VStack,
  SimpleGrid,
  useColorModeValue,
  Alert,
  AlertIcon,
  IconButton,
  useToast,
  InputGroup,
  InputLeftElement,
  Tag,
  TagLabel,
  TagCloseButton,
  Tooltip,
  Divider,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Spinner,
  Switch,
} from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchJobAsync,
  selectJob,
  updateJobAsync,
} from "../../employers/Employers Slice/employerJobs/employerJobSlice";
import { useForm } from "react-hook-form";
import {
  fetchPublicJobTitlesAsync,
  selectJobTitles,
} from "../../AdminPanel/JobPostingSlice/JobTitles/JobTitlesSlice";
import {
  fetchCoreEducationAsync,
  fetchCoreSkillsAsync,
  fetchSuggestedSkillsAsync,
  selectCoreEducations,
  selectCoreSkills,
  selectSelectedSkills,
} from "../../employers/Employers Slice/employersJobCreateSlice/createJobSlice";
import {
  fetchJobDepartmentsAsync,
  selectJobDepartments,
} from "../../AdminPanel/JobPostingSlice/JobDepartment/JobDepartmentSlice";
import {
  fetchPublicCoreLocationsAsync,
  selectPublicCoreLocations,
} from "../../jobSearch/FetchLocationSlice/FetchLocationSlice";
import {
  fetchCitiesAsync,
  fetchCountryAsync,
  fetchStateAsync,
  selectFetchCities,
  selectFetchCountry,
  selectFetchState,
} from "../AboutYou/AboutSlice";
import {
  BuildingOfficeIcon,
  BriefcaseIcon,
  MapPinIcon,
  CalendarIcon,
  UserGroupIcon,
  DocumentTextIcon,
  AcademicCapIcon,
  ClockIcon,
  PhoneIcon,
  CurrencyRupeeIcon,
  PencilIcon,
  ChevronDownIcon,
  InformationCircleIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// Helper: HTML to plain text
const stripHtml = (html) => {
  const doc = new DOMParser().parseFromString(html || "", "text/html");
  return doc.body.textContent || "";
};

// DB-safe cap for additional_pay (Laravel string = 255 chars by default)
const ADDITIONAL_PAY_MAX_CHARS = 255;

// Build a comma-separated string of benefits that fits within maxChars
const packBenefits = (list, maxChars = ADDITIONAL_PAY_MAX_CHARS) => {
  const arr = (list || []).map((s) => (s || "").trim()).filter(Boolean);
  let result = "";
  let usedCount = 0;
  for (let i = 0; i < arr.length; i++) {
    const seg = arr[i];
    const prefix = result.length ? ", " : "";
    if (result.length + prefix.length + seg.length <= maxChars) {
      result += prefix + seg;
      usedCount++;
    } else {
      break;
    }
  }
  return { text: result, usedCount, total: arr.length };
};

// HTML detection and styling for review display
const containsHTML = (str) => {
  if (!str) return false;
  return /<[a-z][\s\S]*>/i.test(str);
};
const richTextStyles = {
  "& .job-description-content": {
    lineHeight: "1.6",
    color: "inherit",
  },
  "& .job-description-content p": {
    marginBottom: "1rem",
    lineHeight: "1.6",
  },
  "& .job-description-content ul, & .job-description-content ol": {
    paddingLeft: "1.5rem",
    marginBottom: "1rem",
    lineHeight: "1.6",
  },
  "& .job-description-content ul": {
    listStyleType: "disc",
  },
  "& .job-description-content ol": {
    listStyleType: "decimal",
  },
  "& .job-description-content li": {
    marginBottom: "0.5rem",
    lineHeight: "1.6",
  },
  "& .job-description-content h1, & .job-description-content h2, & .job-description-content h3, & .job-description-content h4, & .job-description-content h5, & .job-description-content h6": {
    fontWeight: "bold",
    marginTop: "1.5rem",
    marginBottom: "1rem",
    lineHeight: "1.4",
  },
  "& .job-description-content h1": {
    fontSize: "1.5rem",
    fontWeight: "800",
  },
  "& .job-description-content h2": {
    fontSize: "1.25rem",
    fontWeight: "700",
  },
  "& .job-description-content h3": {
    fontSize: "1.125rem",
    fontWeight: "600",
  },
  "& .job-description-content blockquote": {
    borderLeft: "4px solid #e2e8f0",
    paddingLeft: "1rem",
    fontStyle: "italic",
    margin: "1rem 0",
    backgroundColor: "#f7fafc",
    padding: "1rem",
    borderRadius: "0.375rem",
  },
  "& .job-description-content a": {
    color: "#3182ce",
    textDecoration: "underline",
  },
  "& .job-description-content a:hover": {
    color: "#2c5aa0",
  },
  "& .job-description-content code": {
    backgroundColor: "#f1f5f9",
    padding: "0.125rem 0.25rem",
    borderRadius: "0.25rem",
    fontSize: "0.875rem",
    fontFamily: "monospace",
  },
  "& .job-description-content pre": {
    backgroundColor: "#f1f5f9",
    padding: "1rem",
    borderRadius: "0.375rem",
    overflow: "auto",
    marginBottom: "1rem",
  },
};

const JobDetail = ({ label, value, icon }) => (
  <Box p={4} borderWidth="1px" borderRadius="md">
    <HStack mb={2} alignItems="flex-start">
      {icon && React.cloneElement(icon, { className: "h-5 w-5 text-blue-500" })}
      <Text fontWeight="semibold">{label}</Text>
    </HStack>
    {React.isValidElement(value) ? <>{value}</> : <Text>{value ?? "-"}</Text>}
  </Box>
);

const benefitOptions = [
  "Competitive Salary",
  "Performance Bonus / Incentives",
  "Profit Sharing",
  "Stock Options (ESOPs)",
  "Retirement Plans (PF, Pension, 401k etc.)",
  "Annual Increment / Salary Hike",
  "Health Insurance (self + family)",
  "Dental / Vision Insurance",
  "Life Insurance",
  "Disability Insurance",
  "Wellness Programs (yoga, gym, meditation sessions)",
  "Mental Health Support",
  "Paid Leave / Casual Leave / Sick Leave",
  "Paid Holidays / Public Holidays",
  "Maternity & Paternity Leave",
  "Marriage Leave / Compassionate Leave",
  "Sabbatical Leave",
  "Work From Home / Flexible Hours",
  "Flexible Work Arrangements (Remote / Hybrid work)",
  "Transport Facility / Cab Service",
  "Meal Coupons / Free Lunch / Snacks",
  "Company Laptop / Mobile / Internet Allowance",
  "Training & Development Programs",
  "Higher Education Assistance (Tuition Reimbursement)",
  "Certifications & Skill Development Support",
  "Internal Promotions & Career Growth Opportunities",
  "Employee Recognition Awards",
  "Gift Vouchers / Perks for Performance",
  "Team Building Activities / Outings",
  "Annual Parties & Celebrations",
  "Festival Gifts",
  "Flexible Timings",
  "Remote / Hybrid Work Options",
  "Childcare Support / Creche Facility",
  "Family Support Programs",
  "Travel Allowance / Company Car",
  "Housing Allowance",
  "Relocation Assistance",
  "Club Memberships",
  "Discount Programs (on company products or partner brands)",
];

const modules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};
const formats = ["header", "bold", "italic", "underline", "strike", "blockquote", "list", "bullet", "link"];

/**
 * Flatten job.job_educations from API into tags:
 * { uuid: lastNodeUuid, title: "Level / Course / Speciality" }
 */
const deriveEducationTagsFromJob = (jobEducations = []) => {
  const tags = [];
  for (const item of jobEducations) {
    if (item?.core_education_course_specialty) {
      const spec = item.core_education_course_specialty;
      const course = spec?.core_job_education_course;
      const level = course?.core_job_education;
      const label = [level?.title, course?.title, spec?.title].filter(Boolean).join(" / ");
      if (spec?.uuid) tags.push({ uuid: spec.uuid, title: label });
      continue;
    }
    if (item?.core_education_course) {
      const course = item.core_education_course;
      const level = course?.core_job_education;
      const label = [level?.title, course?.title].filter(Boolean).join(" / ");
      if (course?.uuid) tags.push({ uuid: course.uuid, title: label });
      continue;
    }
    if (item?.core_education) {
      const level = item.core_education;
      if (level?.uuid) tags.push({ uuid: level.uuid, title: level.title });
    }
  }
  // dedupe
  const seen = new Set();
  return tags.filter((t) => {
    if (seen.has(t.uuid)) return false;
    seen.add(t.uuid);
    return true;
  });
};
// --- Helpers (add above your component) ---

// Badge color for Job Type
const getJobTypeBadgeColor = (jobType) => {
  switch (jobType) {
    case "Full Time":
      return "green"
    case "Part Time":
      return "purple"
    case "Internship":
      return "orange"
    case "Freelance":
      return "blue"
    case "Temporary":
      return "red"
    default:
      return "gray"
  }
}

// Badge color for Location Type
const getLocationTypeBadgeColor = (locationType) => {
  switch (locationType) {
    case "On Site":
      return "blue"
    case "Remote":
      return "green"
    case "Hybrid":
      return "purple"
    default:
      return "gray"
  }
}

// Normalize boolean-ish values to "Yes"/"No"
const formatBoolean = (value) => {
  const v = typeof value === "string" ? value.toLowerCase() : value
  const truthy = v === true || v === 1 || v === "1" || v === "true" || v === "yes"
  return truthy ? "Yes" : "No"
}
// Helper to render lists that can be array of strings or {title}
const formatItems = (items) => {
  if (!items || !Array.isArray(items) || items.length === 0) return "None specified";
  return items.map((item) => (typeof item === "string" ? item : item?.title)).filter(Boolean).join(", ");
};

const JobReview2 = () => {
  const { isOpen: isOpen2, onOpen: onOpen2, onClose: onClose2 } = useDisclosure();
  const { uuid } = useParams();
  const dispatch = useDispatch();
  const toast = useToast();

  // Selectors
  const job = useSelector(selectJob);
  const jobTitle = useSelector(selectJobTitles);
  const jobTitlesLoading = useSelector((state) => state.jobTitles?.loading);
  const coreLocation = useSelector(selectPublicCoreLocations);
  const coreLocationLoading = useSelector((state) => state.publicCoreLocations?.loading);
  const coreSkills = useSelector(selectCoreSkills);
  const coreEducations = useSelector(selectCoreEducations);
  const suggestedSkills = useSelector(selectSelectedSkills);
  const createJobStatus = useSelector((state) => state.createJobReducers?.status);
  const departments = useSelector(selectJobDepartments);
  const countryList = useSelector(selectFetchCountry);
  const stateList = useSelector(selectFetchState);
  const citiesList = useSelector(selectFetchCities);

  const [selectedCountryId, setSelectedCountryId] = useState("");
  const [selectedStateId, setSelectedStateId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Color mode values
  const accentColor = useColorModeValue("blue.500", "blue.300");
  const formBackground = useColorModeValue("white", "gray.700");
  const sectionBgColor = useColorModeValue("blue.50", "blue.900");
  const borderColor = useColorModeValue("blue.100", "blue.700");

  // RHF
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useForm({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      job_type: "Full Time",
      work_location_type: "On Site",
      job_shift: "Day Shift",
      salary_per_type: "Monthly",
      joining_amount_required: false,
      pay_type: "Do Not Disclose",
      salary_not_disclose: true,
      upload_cv: true,
      job_apply_age: "18-25",
      min_job_exp: "0",
      max_job_exp: "3",
      candidate_contact_via: "By call",
      interview_type: "In-Person",
    },
  });

  // Local UI states
  const [selectedJobTitle, setSelectedJobTitle] = useState(null);
  const [jobTitleKeyword, setJobTitleKeyword] = useState("");
  const [isPlannedStart, setIsPlannedStart] = useState(false);
  const [payByOption, setPayByOption] = useState("Do Not Disclose");
  const [quillContent, setQuillContent] = useState("");
  const [benefits, setBenefits] = useState([]);

  // Tag-like selections
  const [location, setLocation] = useState([]);
  const [locationId, setLocationId] = useState([]);
  const [skills, setSkills] = useState([]);
  const [skillsId, setSkillsId] = useState([]);
  const [education, setEducation] = useState([]);
  const [educationId, setEducationId] = useState([]);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [locationError, setLocationError] = useState("");
  const [skillsError, setSkillsError] = useState("");
  const [educationError, setEducationError] = useState("");
  const [skillSearch, setSkillSearch] = useState("");
  const [locationSearch, setLocationSearch] = useState("");

  // Hierarchical education picker state (Level → Course → Speciality)
  const [selectedEduLevel, setSelectedEduLevel] = useState(null);
  const [selectedEduCourse, setSelectedEduCourse] = useState(null);
  const [selectedEduSpeciality, setSelectedEduSpeciality] = useState(null);

  // Derived lists for hierarchical selects
  const levels = useMemo(() => (Array.isArray(coreEducations) ? coreEducations : []), [coreEducations]);
  const courses = useMemo(
    () => (selectedEduLevel?.core_job_education_course || []),
    [selectedEduLevel]
  );
  const specialities = useMemo(
    () => (selectedEduCourse?.core_job_education_course_specialty || []),
    [selectedEduCourse]
  );

  const resetCourseAndBelow = () => {
    setSelectedEduCourse(null);
    setSelectedEduSpeciality(null);
  };
  const resetSpeciality = () => setSelectedEduSpeciality(null);
  const buildEduLabel = (level, course, spec) =>
    [level?.title, course?.title, spec?.title].filter(Boolean).join(" / ");

  // Add the selected education chain as one tag
  const addSelectedEducation = () => {
    if (!selectedEduLevel) return;

    const finalObj = selectedEduSpeciality || selectedEduCourse || selectedEduLevel;
    const finalUuid = finalObj.uuid;
    const label = buildEduLabel(selectedEduLevel, selectedEduCourse, selectedEduSpeciality);

    // prevent duplicates
    if (educationId.includes(finalUuid)) return;

    const newItem = { uuid: finalUuid, title: label };
    const updatedEducation = [...education, newItem];
    const updatedIds = [...educationId, finalUuid];

    setEducation(updatedEducation);
    setEducationId(updatedIds);
    setValue("job_education", updatedIds, { shouldValidate: true });

    // clear selection to enforce "start from level again"
    setSelectedEduLevel(null);
    resetCourseAndBelow();
  };

  const watchSalaryMin = watch("salary_min_amount");
  const watchSalaryMax = watch("salary_max_amount");
  const watchMinExp = watch("min_job_exp");
  const watchMaxExp = watch("max_job_exp");

  const isSuggestLoading = createJobStatus === "loading" && (skillSearch || "").trim().length > 0;
  const today = new Date().toISOString().split("T")[0];

  // Effects: fetch base data
  useEffect(() => {
    if (uuid) dispatch(fetchJobAsync({ uuid }));
    dispatch(fetchPublicJobTitlesAsync());
    dispatch(fetchJobDepartmentsAsync());
    dispatch(fetchCoreEducationAsync());
    dispatch(fetchPublicCoreLocationsAsync());
    dispatch(fetchCoreSkillsAsync());
    if ((countryList || []).length === 0) dispatch(fetchCountryAsync());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, uuid]);

  // Sync form and local states from job
  useEffect(() => {
    if (!job) return;

    // salary disclosure
    const salaryDisclosed = !!job?.salary_min_amount && !!job?.salary_max_amount && !job?.salary_not_disclose;

    setPayByOption(salaryDisclosed ? "Salary Range" : "Do Not Disclose");
    setValue("pay_type", salaryDisclosed ? "Salary Range" : "Do Not Disclose");
    setValue("salary_not_disclose", !salaryDisclosed);

    setValue("salary_min_amount", job?.salary_min_amount ?? "");
    setValue("salary_max_amount", job?.salary_max_amount ?? "");
    setValue("salary_per_type", job?.salary_per_type || "Monthly");

    const benefitsArr = (job?.additional_pay || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    setBenefits(Array.from(new Set(benefitsArr)));

    setIsPlannedStart(!!job?.is_planned_start_date);
    setValue("is_planned_start_date", !!job?.is_planned_start_date);
    setValue("plan_start_date", job?.plan_start_date || null);

    setValue("upload_cv", !!job?.upload_cv);
    setValue("job_apply_age", job?.job_apply_age || "18-25");
    setValue("candidate_gender", job?.candidate_gender || "Both");
    setValue("candidate_contact_via", job?.candidate_contact_via || "By call");

    // Prefill deadline to today if missing/past
    const todayStr = new Date().toISOString().split("T")[0];
    const validDeadline = job?.job_deadline && job.job_deadline >= todayStr ? job.job_deadline : todayStr;
    setValue("job_deadline", validDeadline);

    setValue("hiring_company_name", job?.hiring_company_name || "");
    setValue("job_name", job?.job_name || "");
    setValue("job_tag", job?.job_tag || "");
    setValue("job_type", job?.job_type || "Full Time");
    setValue("work_location_type", job?.work_location_type || "On Site");
    setValue("job_shift", job?.job_shift || "Day Shift");

    setValue("address1", job?.address1 || "");
    setValue("address2", job?.address2 || "");
    setValue("pincode", job?.pincode || "");

    setValue("no_of_hire", job?.no_of_hire ?? "");
    setValue("joining_amount_required", !!job?.joining_amount_required);
    setValue("joining_amount", job?.joining_amount ?? "");

    // Experience: ensure RHF has the job values
    if (job?.min_job_exp != null) setValue("min_job_exp", String(job.min_job_exp));
    if (job?.max_job_exp != null) setValue("max_job_exp", String(job.max_job_exp));

    // Job description (Quill stores HTML)
    setQuillContent(job?.job_description || "");
    setValue("job_description", job?.job_description || "");

    // JSON-ish select values
    if (job?.job_title?.uuid) {
      setSelectedJobTitle({ uuid: job.job_title.uuid, title: job.job_title.title });
      setValue(
        "job_title_uuid",
        JSON.stringify({ uuid: job.job_title.uuid, title: job.job_title.title })
      );
    }
    if (job?.department?.uuid) {
      setValue("department_uuid", JSON.stringify({ uuid: job.department.uuid, title: job.department.title }));
    }
    if (job?.country?.uuid) {
      setValue("country_uuid", JSON.stringify({ uuid: job.country.uuid, title: job.country.name }));
      setSelectedCountryId(job.country.uuid);
      dispatch(fetchStateAsync(job.country.uuid));
    }
    if (job?.state?.uuid) {
      setValue("state_uuid", JSON.stringify({ uuid: job.state.uuid, title: job.state.name }));
      setSelectedStateId(job.state.uuid);
      dispatch(fetchCitiesAsync(job.state.uuid));
    }
    if (job?.city?.uuid) {
      setValue("city_uuid", JSON.stringify({ uuid: job.city.uuid, title: job.city.name }));
    }

    // Prefill tags
    if (job?.job_locations) {
      const locs = job.job_locations.map((l) => l.job_location).filter(Boolean);
      setLocation(locs);
      setLocationId(locs.map((l) => l.uuid));
    }
    if (job?.job_skills) {
      const sks = job.job_skills.map((s) => s.job_skill).filter(Boolean);
      setSkills(sks);
      setSkillsId(sks.map((s) => s.uuid));
    }
    if (job?.job_educations) {
      // Flatten API response to tags with last selected UUID and chain title
      const edTags = deriveEducationTagsFromJob(job.job_educations);
      setEducation(edTags);
      setEducationId(edTags.map((e) => e.uuid));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job, dispatch]);

  // Pack benefits to DB-safe string and keep RHF in sync
  const packedBenefits = useMemo(() => packBenefits(benefits, ADDITIONAL_PAY_MAX_CHARS), [benefits]);
  useEffect(() => {
    setValue("additional_pay", packedBenefits.text, { shouldValidate: true });
  }, [packedBenefits, setValue]);

  // Keep RHF arrays in sync
  useEffect(() => {
    setValue("job_location", locationId, { shouldValidate: true });
  }, [locationId, setValue]);
  useEffect(() => {
    setValue("job_skills", skillsId, { shouldValidate: true });
  }, [skillsId, setValue]);
  useEffect(() => {
    setValue("job_education", educationId, { shouldValidate: true });
  }, [educationId, setValue]);

  // Validation helpers
  useEffect(() => {
    if (watchSalaryMin && watchSalaryMax) trigger("salary_max_amount");
  }, [watchSalaryMin, watchSalaryMax, trigger]);

  useEffect(() => {
    if (skills.length === 0 && formSubmitted) setSkillsError("At least one skill is required");
    else setSkillsError("");
  }, [skills, formSubmitted]);

  useEffect(() => {
    if (location.length === 0 && formSubmitted) setLocationError("At least one job location is required");
    else setLocationError("");
  }, [location, formSubmitted]);

  useEffect(() => {
    if (education.length === 0 && formSubmitted)
      setEducationError("At least one education requirement is required");
    else setEducationError("");
  }, [education, formSubmitted]);

  // Debounced searches
  useEffect(() => {
    const handler = setTimeout(() => {
      const q = (jobTitleKeyword || "").trim();
      dispatch(fetchPublicJobTitlesAsync(q));
    }, 500);
    return () => clearTimeout(handler);
  }, [jobTitleKeyword, dispatch]);

  useEffect(() => {
    const handler = setTimeout(() => {
      const q = (skillSearch || "").trim();
      if (q.length > 0) dispatch(fetchSuggestedSkillsAsync(q));
    }, 500);
    return () => clearTimeout(handler);
  }, [skillSearch, dispatch]);

  useEffect(() => {
    const handler = setTimeout(() => {
      const q = (locationSearch || "").trim();
      if (q.length > 0) dispatch(fetchPublicCoreLocationsAsync({ keyword: q }));
      else dispatch(fetchPublicCoreLocationsAsync());
    }, 500);
    return () => clearTimeout(handler);
  }, [locationSearch, dispatch]);

  // Country/State changes (JSON payload)
  const countryChange = (e) => {
    const val = e.target.value;
    if (!val) return;
    try {
      const obj = JSON.parse(val);
      setSelectedCountryId(obj.uuid);
      setSelectedStateId("");
      setValue("state_uuid", "", { shouldValidate: true });
      setValue("city_uuid", "", { shouldValidate: true });
      dispatch(fetchStateAsync(obj.uuid));
      trigger("country_uuid");
    } catch { }
  };
  const stateChange = (e) => {
    const val = e.target.value;
    if (!val) return;
    try {
      const obj = JSON.parse(val);
      setSelectedStateId(obj.uuid);
      setValue("city_uuid", "", { shouldValidate: true });
      dispatch(fetchCitiesAsync(obj.uuid));
      trigger("state_uuid");
    } catch { }
  };

  // Job Title select handlers
  const handleJobTitleSelect = (item) => {
    const payload = JSON.stringify({ uuid: item.uuid, title: item.title });
    setSelectedJobTitle(item);
    setValue("job_title_uuid", payload, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
  };

  // Pay type select
  const handlePaySelectChange = (e) => {
    const option = e.target.value;
    setPayByOption(option);
    const isRange = option === "Salary Range";
    setValue("pay_type", isRange ? "Salary Range" : "Do Not Disclose", { shouldValidate: true });
    setValue("salary_not_disclose", !isRange, { shouldValidate: true });

    if (isRange) {
      setTimeout(() => {
        trigger("salary_min_amount");
        trigger("salary_max_amount");
      }, 0);
    } else {
      setValue("salary_min_amount", "", { shouldValidate: true });
      setValue("salary_max_amount", "", { shouldValidate: true });
      setValue("salary_per_type", "Monthly", { shouldValidate: true });
    }
  };

  // Quill handler
  const handleQuillChange = (content) => {
    setQuillContent(content);
    setValue("job_description", content, { shouldValidate: true });
    setTimeout(() => trigger("job_description"), 0);
  };

  const addBenefit = (value) => {
    if (!value) return;
    setBenefits((prev) => Array.from(new Set([...prev, value])));
  };
  const removeBenefit = (idx) => {
    setBenefits((prev) => prev.filter((_, i) => i !== idx));
  };

  // Location/skills handlers
  const handleSelectLocationItem = (item) => {
    if (!item) return;
    if (location.some((l) => l.uuid === item.uuid)) return;
    const updated = [...location, item];
    setLocation(updated);
    setLocationId(updated.map((l) => l.uuid));
  };
  const removeLocation = (index) => {
    const updated = location.filter((_, i) => i !== index);
    setLocation(updated);
    setLocationId(updated.map((l) => l.uuid));
  };

  const skillsListToShow = useMemo(() => {
    const q = (skillSearch || "").trim();
    if (q.length > 0 && Array.isArray(suggestedSkills)) return suggestedSkills;
    return Array.isArray(coreSkills) ? coreSkills : [];
  }, [skillSearch, suggestedSkills, coreSkills]);

  const handleSelectSkillItem = (item) => {
    if (!item) return;
    if (skills.some((s) => s.uuid === item.uuid)) return;
    const updated = [...skills, item];
    setSkills(updated);
    setSkillsId(updated.map((s) => s.uuid));
  };
  const removeSkill = (index) => {
    const updated = skills.filter((_, i) => i !== index);
    setSkills(updated);
    setSkillsId(updated.map((s) => s.uuid));
  };

  const removeEducation = (index) => {
    const updated = education.filter((_, i) => i !== index);
    setEducation(updated);
    setEducationId(updated.map((ed) => ed.uuid));
  };

  const openUpdateModal = () => onOpen2();

  // Submit error handler
  const onInvalid = (errs) => {
    const firstKey = Object.keys(errs || {})[0];
    const firstMsg = errs?.[firstKey]?.message || "Please fix the highlighted fields";
    toast({
      title: "Fix validation errors",
      description: firstMsg,
      status: "error",
      duration: 3000,
      isClosable: true,
      position: "top-right",
    });
    console.warn("RHF validation errors:", errs);
  };

  // Submit
  const handleUpdate = async (data) => {
    setFormSubmitted(true);

    if (location.length === 0 || skills.length === 0 || education.length === 0) {
      toast({
        title: "Please complete required selections",
        description: "Add at least one location, skill, and education item.",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    // Validate job description length (plain text)
    const plain = stripHtml(quillContent || "");
    if (plain.trim().length < 50) {
      toast({
        title: "Description too short",
        description: "Job description must be at least 50 characters.",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const jobTitleObj = JSON.parse(data.job_title_uuid || "null");
      const deptObj = JSON.parse(data.department_uuid || "null");
      const countryObj = JSON.parse(data.country_uuid || "null");
      const stateObj = JSON.parse(data.state_uuid || "null");
      const cityObj = JSON.parse(data.city_uuid || "null");

      // Normalize and PACK benefits to fit DB column
      const packed = packBenefits(benefits, ADDITIONAL_PAY_MAX_CHARS);
      if (packed.usedCount < packed.total) {
        toast({
          title: "Too many benefits selected",
          description: `Saved the first ${packed.usedCount} of ${packed.total} benefits to fit the limit (${ADDITIONAL_PAY_MAX_CHARS} chars).`,
          status: "info",
          duration: 4000,
          isClosable: true,
          position: "top-right",
        });
      }

      const isRange = payByOption === "Salary Range";
      const toNum = (v) => (v === "" || v == null ? null : Number(v));
      const toNumFlag = (v) => (v === true || v === 1 || v === "1" || v === "true" ? 1 : 0);

      const newData = {
        department_uuid: deptObj?.uuid || job?.department?.uuid || "",
        hiring_company_name: data.hiring_company_name,
        job_title_uuid: jobTitleObj?.uuid || job?.job_title?.uuid || "",
        work_location_type: data.work_location_type,

        // arrays -> using uuid + title (title may be "Level / Course / Speciality")
        job_location: location.map((loc) => ({ uuid: loc.uuid, title: loc.title })),
        job_education: education.map((ed) => ({ uuid: ed.uuid, title: ed.title })),
        job_skills: skills.map((sk) => ({ uuid: sk.uuid, title: sk.title })),

        job_type: data.job_type,
        status: data.status || job?.status || "active",
        job_name: data.job_name,
        job_apply_age: data.job_apply_age,
        job_shift: data.job_shift,
        job_tag: data.job_tag,

        // Salary & Pay
        pay_type: isRange ? "Salary Range" : "Do Not Disclose",
        additional_pay: packed.text,
        salary_not_disclose: isRange ? 0 : 1, // numeric flag
        salary_min_amount: isRange ? toNum(data.salary_min_amount) : null,
        salary_max_amount: isRange ? toNum(data.salary_max_amount) : null,
        salary_exact_amount: null,
        salary_per_type: isRange ? data.salary_per_type : null,

        candidate_gender: data.candidate_gender,
        job_description: quillContent, // HTML from Quill

        joining_amount_required: toNumFlag(data.joining_amount_required),
        joining_amount: toNum(data.joining_amount),

        upload_cv: toNumFlag(data.upload_cv),
        job_deadline: data.job_deadline || null,
        min_job_exp: toNum(data.min_job_exp),
        max_job_exp: toNum(data.max_job_exp),
        interview_type: data.interview_type,
        candidate_contact_via: data.candidate_contact_via,

        address1: data.address1 || "",
        address2: data.address2 || "",
        city_uuid: cityObj?.uuid,
        state_uuid: stateObj?.uuid,
        country_uuid: countryObj?.uuid,
        pincode: data.pincode,

        is_planned_start_date: toNumFlag(data.is_planned_start_date),
        plan_start_date: data.is_planned_start_date ? data.plan_start_date || null : null,

        no_of_hire: toNum(data.no_of_hire),

        is_published: job?.is_published || false,
      };

      await dispatch(
        updateJobAsync({
          uuid: job?.uuid,
          jobData: newData,
        })
      ).unwrap();

      await dispatch(fetchJobAsync({ uuid }));
      onClose2();

      toast({
        title: "Job Updated Successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      console.error("Failed to update job", error);
      toast({
        title: "Error Updating Job",
        description: error?.message || "Failed to update job",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!job) {
    return (
      <Box minH="100vh" py={12} className="bg-mainBg">
        <Container maxW="6xl">
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            Job data not found.
          </Alert>
        </Container>
      </Box>
    );
  }

  // Education titles (read-only view) from API
  const educationDisplayTitles = deriveEducationTagsFromJob(job?.job_educations || []).map((x) => x.title);

  // Experience display on read view
  const experienceDisplay =
    Number(job?.min_job_exp) === 0 && Number(job?.max_job_exp) === 0
      ? "Fresher"
      : job?.min_job_exp != null && job?.max_job_exp != null
        ? `${job?.min_job_exp} - ${job?.max_job_exp} years`
        : "-";

  return (
    <Box minH="100vh" py={12} className="bg-mainBg">
      <Container maxW="6xl">
        <Box bg={formBackground} borderRadius="xl" shadow="xl" p={6} transition="all 0.3s">
          <Flex justify="space-between" align="center" mb={8}>
            <Heading as="h2" size="xl" color={accentColor}>
              Job Review
            </Heading>
            <IconButton icon={<PencilIcon className="h-5 w-5" />} onClick={openUpdateModal} colorScheme="blue" aria-label="Edit Job" />
          </Flex>

          <Alert status="info" mb={6} borderRadius="md">
            <AlertIcon />
            Review your job details carefully before making changes.
          </Alert>

          <VStack spacing={6} align="stretch">
            <Box p={4} borderLeft="4px solid" borderColor={accentColor} bg={sectionBgColor} borderRadius="md">
              <Heading as="h3" size="md" mb={2}>
                Basic Job Information
              </Heading>
              <Text color="gray.600">Review the fundamental details about the position.</Text>
            </Box>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <JobDetail label="Company" value={job?.hiring_company_name} icon={<BuildingOfficeIcon />} />
              <JobDetail label="Job Title" value={job?.job_title?.title} icon={<BriefcaseIcon />} />
              <JobDetail label="Designation" value={job?.job_name} icon={<BriefcaseIcon />} />
              <JobDetail label="Department" value={job?.department?.title} icon={<BriefcaseIcon />} />
              <JobDetail
                label="Job Type"
                value={
                  <Badge colorScheme={getJobTypeBadgeColor(job?.job_type)} px={2} py={1} borderRadius="full">
                    {job?.job_type || "-"}
                  </Badge>
                }
                icon={<BriefcaseIcon />}
              />
              <JobDetail
                label="Location Type"
                value={
                  <Badge colorScheme={getLocationTypeBadgeColor(job?.work_location_type)} px={2} py={1} borderRadius="full">
                    {job?.work_location_type || "-"}
                  </Badge>
                }
                icon={<MapPinIcon />}
              />
              <JobDetail label="Shift" value={job?.job_shift} icon={<ClockIcon />} />
              <JobDetail label="Number of Openings" value={job?.no_of_hire || "Not specified"} icon={<UserGroupIcon />} />
            </SimpleGrid>

            <Box p={4} borderLeft="4px solid" borderColor={accentColor} bg={sectionBgColor} borderRadius="md" mt={4}>
              <Heading as="h3" size="md" mb={2}>
                Job Location
              </Heading>
              <Text color="gray.600">Review the address details for this position.</Text>
            </Box>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <JobDetail
                label="Address"
                value={`${job?.address1 || ""}${job?.address2 ? `, ${job?.address2}` : ""}` || "-"}
                icon={<MapPinIcon />}
              />
              <JobDetail
                label="Location"
                value={[job?.city?.name, job?.state?.name, job?.country?.name].filter(Boolean).join(", ") || "-"}
                icon={<MapPinIcon />}
              />
              <JobDetail label="Postal Code" value={job?.pincode} icon={<MapPinIcon />} />
              <JobDetail
                label="Job Posting Locations"
                value={formatItems(job?.job_locations?.map((loc) => loc.job_location))}
                icon={<MapPinIcon />}
              />
            </SimpleGrid>

            <Box p={4} borderLeft="4px solid" borderColor={accentColor} bg={sectionBgColor} borderRadius="md" mt={4}>
              <Heading as="h3" size="md" mb={2}>
                Salary & Benefits
              </Heading>
              <Text color="gray.600">Review salary and benefits information.</Text>
            </Box>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {job?.salary_min_amount && job?.salary_max_amount ? (
                <JobDetail
                  label="Salary Range"
                  value={`${job?.salary_min_amount} - ${job?.salary_max_amount} (${job?.salary_per_type})`}
                  icon={<CurrencyRupeeIcon />}
                />
              ) : (
                <JobDetail label="Salary" value="Not disclosed" icon={<CurrencyRupeeIcon />} />
              )}
              <JobDetail
                label="Joining Fee Required"
                value={
                  <Badge colorScheme={job?.joining_amount_required ? "red" : "green"} px={2} py={1} borderRadius="full">
                    {formatBoolean(job?.joining_amount_required)}
                  </Badge>
                }
                icon={<CurrencyRupeeIcon />}
              />
              {job?.additional_pay && (
                <JobDetail label="Additional Benefits" value={job?.additional_pay} icon={<CurrencyRupeeIcon />} />
              )}
            </SimpleGrid>

            <Box p={4} borderLeft="4px solid" borderColor={accentColor} bg={sectionBgColor} borderRadius="md" mt={4}>
              <Heading as="h3" size="md" mb={2}>
                Job Description
              </Heading>
              <Text color="gray.600">Review the job description and requirements.</Text>
            </Box>

            <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor} sx={richTextStyles}>
              <HStack mb={2} alignItems="flex-start">
                <DocumentTextIcon className="h-5 w-5 text-blue-500" />
                <Text fontWeight="semibold">Job Description</Text>
              </HStack>
              <Box p={4} bg="gray.50" borderRadius="md" mt={2} className="job-description-container">
                {job?.job_description && containsHTML(job?.job_description) ? (
                  <Box
                    dangerouslySetInnerHTML={{ __html: job?.job_description }}
                    className="job-description-content"
                  />
                ) : (
                  <Text whiteSpace="pre-wrap" className="job-description-content">
                    {job?.job_description || "No job description provided"}
                  </Text>
                )}
              </Box>
            </Box>

            <Box p={4} borderLeft="4px solid" borderColor={accentColor} bg={sectionBgColor} borderRadius="md" mt={4}>
              <Heading as="h3" size="md" mb={2}>
                Skills and Qualifications
              </Heading>
              <Text color="gray.600">Review the required skills and qualifications.</Text>
            </Box>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <JobDetail
                label="Required Skills"
                value={formatItems(job?.job_skills?.map((skill) => skill.job_skill))}
                icon={<AcademicCapIcon />}
              />
              <JobDetail
                label="Education Requirements"
                value={formatItems(educationDisplayTitles)}
                icon={<AcademicCapIcon />}
              />
              <JobDetail
                label="Experience Required"
                value={experienceDisplay}
                icon={<BriefcaseIcon />}
              />
              <JobDetail label="Age Range" value={job?.job_apply_age} icon={<UserGroupIcon />} />
              <JobDetail label="Gender" value={job?.candidate_gender} icon={<UserGroupIcon />} />
            </SimpleGrid>

            <Box p={4} borderLeft="4px solid" borderColor={accentColor} bg={sectionBgColor} borderRadius="md" mt={4}>
              <Heading as="h3" size="md" mb={2}>
                Application Process
              </Heading>
              <Text color="gray.600">Review the application and interview details.</Text>
            </Box>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <JobDetail label="Application Deadline" value={job?.job_deadline} icon={<CalendarIcon />} />
              {job?.is_planned_start_date ? (
                <JobDetail label="Planned Start Date" value={job?.plan_start_date} icon={<CalendarIcon />} />
              ) : null}
              <JobDetail
                label="Interview Type"
                value={
                  <Badge colorScheme={job?.interview_type === "In-Person" ? "orange" : "blue"} px={2} py={1} borderRadius="full">
                    {job?.interview_type || "-"}
                  </Badge>
                }
                icon={<BriefcaseIcon />}
              />
              <JobDetail label="Contact Preference" value={job?.candidate_contact_via} icon={<PhoneIcon />} />
              <JobDetail
                label="CV Required"
                value={
                  <Badge colorScheme={job?.upload_cv ? "green" : "red"} px={2} py={1} borderRadius="full">
                    {formatBoolean(job?.upload_cv)}
                  </Badge>
                }
                icon={<DocumentTextIcon />}
              />
            </SimpleGrid>
          </VStack>
        </Box>
      </Container>

      {/* Edit Modal */}
      <Modal isOpen={isOpen2} onClose={onClose2} size="5xl" >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader className="text-3xl font-bold">Edit Job Details</ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmit(handleUpdate, onInvalid)}>
            <ModalBody>
              {/* Basic Job Information */}
              <Box p={4} borderLeft="4px solid" borderColor={accentColor} bg="blue.50" borderRadius="md" mb={4}>
                <Heading as="h3" size="md" mb={2}>
                  Basic Job Information
                </Heading>
                <Text color="gray.600">Edit the fundamental details about the position.</Text>
              </Box>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                <FormControl isRequired isInvalid={errors.hiring_company_name}>
                  <FormLabel>Company name</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                    </InputLeftElement>
                    <Input
                      {...register("hiring_company_name", {
                        required: "Company name is required",
                        minLength: { value: 2, message: "At least 2 characters" },
                        maxLength: { value: 100, message: "Max 100 characters" },
                      })}
                      pl={10}
                      placeholder="Company name"
                      isReadOnly
                    />
                  </InputGroup>
                </FormControl>

                <FormControl isRequired isInvalid={errors.job_title_uuid}>
                  <FormLabel>Job Title</FormLabel>
                  {/* Hidden for RHF value */}
                  <input type="hidden" {...register("job_title_uuid", { required: "Job title is required" })} />

                  <Menu autoSelect={false} closeOnSelect>
                    <MenuButton
                      as={Button}
                      variant="outline"
                      rightIcon={<ChevronDownIcon className="h-5 w-5" />}
                      width="100%"
                      justifyContent="space-between"
                      fontWeight="normal"
                      borderColor={errors.job_title_uuid ? "red.500" : "gray.200"}
                    >
                      {selectedJobTitle?.title || "Select a job title"}
                    </MenuButton>
                    <MenuList p={3} minW={{ base: "100%", md: "sm" }}>
                      <Input
                        size="sm"
                        placeholder="Type to search job titles..."
                        value={jobTitleKeyword}
                        onChange={(e) => setJobTitleKeyword(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                        autoFocus
                      />
                      <Box maxH="260px" overflowY="auto" mt={2}>
                        {jobTitlesLoading ? (
                          <HStack p={3}>
                            <Spinner size="sm" />
                            <Text>Loading...</Text>
                          </HStack>
                        ) : jobTitle && jobTitle.length > 0 ? (
                          jobTitle.map((item) => (
                            <MenuItem key={item.uuid} onClick={() => handleJobTitleSelect(item)}>
                              {item.title}
                            </MenuItem>
                          ))
                        ) : (
                          <Text p={3} color="gray.500">
                            No results
                          </Text>
                        )}
                      </Box>
                    </MenuList>
                  </Menu>
                </FormControl>
              </SimpleGrid>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mt={4}>
                <FormControl isRequired isInvalid={errors.job_name}>
                  <FormLabel>Designation</FormLabel>
                  <Input
                    {...register("job_name", {
                      required: "Designation is required",
                      minLength: { value: 2, message: "At least 2 characters" },
                      maxLength: { value: 100, message: "Max 100 characters" },
                    })}
                    placeholder="E.g., Senior Frontend Developer"
                  />
                </FormControl>

                <FormControl isInvalid={errors.job_tag}>
                  <FormLabel>Level</FormLabel>
                  <Select
                    {...register("job_tag", { validate: () => true })}
                    placeholder="Select job level"
                  >
                    <option value="freelance">Freelance</option>
                    <option value="internship">Internship</option>
                    <option value="flexible location">Flexible Location</option>
                    <option value="entry-level">Entry-Level</option>
                    <option value="mid-level">Mid-Level</option>
                    <option value="senior-level">Senior-Level</option>
                    <option value="immediate joiner">Immediate Joiner</option>
                    <option value="flexible hours">Flexible Hours</option>
                  </Select>
                </FormControl>
              </SimpleGrid>

              <FormControl isRequired isInvalid={errors.department_uuid} mt={4}>
                <FormLabel>Department</FormLabel>
                <Select
                  {...register("department_uuid", { required: "Department is required" })}
                  placeholder="Select Job Department"
                >
                  {departments?.map((d) => (
                    <option key={d.uuid} value={JSON.stringify({ uuid: d.uuid, title: d.title })}>
                      {d.title}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mt={4}>
                <FormControl isRequired isInvalid={errors.job_type}>
                  <FormLabel>Job Type</FormLabel>
                  <Select {...register("job_type", { required: "Please select a job type" })} defaultValue="Full Time">
                    <option value="Full Time">Full Time</option>
                    <option value="Part Time">Part Time</option>
                    <option value="Internship">Internship</option>
                    <option value="Freelance">Freelance</option>
                    <option value="Temporary">Temporary</option>
                  </Select>
                </FormControl>

                <FormControl isRequired isInvalid={errors.work_location_type}>
                  <FormLabel>Location Type</FormLabel>
                  <Select
                    {...register("work_location_type", { required: "Please select a location type" })}
                    defaultValue="On Site"
                  >
                    <option value="On Site">On Site</option>
                    <option value="Remote">Remote</option>
                    <option value="Hybrid">Hybrid</option>
                  </Select>
                </FormControl>

                <FormControl isRequired isInvalid={errors.job_shift}>
                  <FormLabel>Shift</FormLabel>
                  <Select {...register("job_shift", { required: "Please select a shift type" })} defaultValue="Day Shift">
                    <option value="Day Shift">Day Shift</option>
                    <option value="Morning Shift">Morning Shift</option>
                    <option value="Night Shift">Night Shift</option>
                    <option value="Mon - Fri">Mon - Fri</option>
                    <option value="UK Shift">UK Shift</option>
                  </Select>
                </FormControl>
              </SimpleGrid>

              {/* Job Location */}
              <Box p={4} borderLeft="4px solid" borderColor={accentColor} bg="blue.50" borderRadius="md" mt={6}>
                <Heading as="h3" size="md" mb={2}>
                  Job Location
                </Heading>
                <Text color="gray.600">Provide address details for this position.</Text>
              </Box>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mt={4}>
                <FormControl isRequired isInvalid={errors.address1}>
                  <FormLabel>Address (apartment/floor/building)</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <MapPinIcon className="h-5 w-5 text-gray-400" />
                    </InputLeftElement>
                    <Input
                      {...register("address1", {
                        required: "Address is required",
                        minLength: { value: 5, message: "Min 5 characters" },
                        maxLength: { value: 200, message: "Max 200 characters" },
                      })}
                      pl={10}
                      placeholder="Street address"
                    />
                  </InputGroup>
                </FormControl>

                <FormControl isInvalid={errors.address2}>
                  <FormLabel>Street Address</FormLabel>
                  <Input
                    {...register("address2", {
                      maxLength: { value: 200, message: "Max 200 characters" },
                    })}
                    placeholder="Apt, Suite, Building (optional)"
                  />
                </FormControl>
              </SimpleGrid>

              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mt={4}>
                <FormControl isRequired isInvalid={errors.country_uuid}>
                  <FormLabel>Country</FormLabel>
                  <Select
                    {...register("country_uuid", { required: "Country is required" })}
                    onChange={countryChange}
                    placeholder="Select country"
                  >
                    {countryList?.map((item) => (
                      <option key={item.uuid} value={JSON.stringify({ uuid: item.uuid, title: item.name })}>
                        {item.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl isRequired isInvalid={errors.state_uuid}>
                  <FormLabel>State/Province</FormLabel>
                  <Select
                    {...register("state_uuid", {
                      required: "State is required",
                      validate: () => (selectedCountryId ? true : "Please select a country first"),
                    })}
                    onChange={stateChange}
                    placeholder="Select state"
                    isDisabled={!selectedCountryId}
                  >
                    {stateList?.map((state) => (
                      <option key={state.uuid} value={JSON.stringify({ uuid: state.uuid, title: state.name })}>
                        {state.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl isRequired isInvalid={errors.city_uuid}>
                  <FormLabel>City</FormLabel>
                  <Select
                    {...register("city_uuid", {
                      required: "City is required",
                      validate: () => (selectedStateId ? true : "Please select a state first"),
                    })}
                    placeholder="Select city"
                    isDisabled={!selectedStateId}
                  >
                    {Array.isArray(citiesList) &&
                      citiesList.map((c) => (
                        <option key={c.uuid} value={JSON.stringify({ uuid: c.uuid, title: c.name })}>
                          {c.name}
                        </option>
                      ))}
                  </Select>
                </FormControl>
              </SimpleGrid>

              <FormControl isRequired isInvalid={errors.pincode} mt={4}>
                <FormLabel>Postal/Zip Code</FormLabel>
                <Input
                  {...register("pincode", {
                    required: "Postal code is required",
                    pattern: { value: /^\d{5,6}$/, message: "Enter valid 5-6 digit code" },
                  })}
                  onChange={(e) => {
                    let value = e.target.value.replace(/\D/g, "");
                    if (value.length > 6) value = value.slice(0, 6);
                    setValue("pincode", value, { shouldValidate: true });
                  }}
                  placeholder="Enter 5-6 digit code"
                />
              </FormControl>

              {/* Salary & Benefits */}
              <Box p={4} borderLeft="4px solid" borderColor={accentColor} bg="blue.50" borderRadius="md" mt={6}>
                <Heading as="h3" size="md" mb={2}>
                  Salary & Benefits Details
                </Heading>
                <Text color="gray.600">Provide salary and benefits information.</Text>
              </Box>

              {/* Hidden RHF tracking */}
              <input type="hidden" {...register("pay_type")} />
              <input type="hidden" {...register("salary_not_disclose")} />

              <FormControl mt={4}>
                <FormLabel>Pay Type</FormLabel>
                <Select defaultValue={payByOption} onChange={handlePaySelectChange}>
                  <option value="Do Not Disclose">Do Not Disclose</option>
                  <option value="Salary Range">Salary Range (Min to Max)</option>
                </Select>
              </FormControl>

              {payByOption === "Salary Range" && (
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mt={4}>
                  <FormControl isRequired isInvalid={errors.salary_min_amount}>
                    <FormLabel>Minimum Salary</FormLabel>
                    <InputGroup>
                      <InputLeftElement pointerEvents="none">
                        <CurrencyRupeeIcon className="h-5 w-5 text-gray-400" />
                      </InputLeftElement>
                      <Input
                        {...register("salary_min_amount", {
                          required: "Minimum salary is required",
                          pattern: { value: /^[0-9]+$/, message: "Enter a valid number" },
                          min: { value: 1, message: "Must be greater than 0" },
                        })}
                        pl={10}
                        placeholder="e.g., 50000"
                      />
                    </InputGroup>
                  </FormControl>

                  <FormControl isRequired isInvalid={errors.salary_max_amount}>
                    <FormLabel>Maximum Salary</FormLabel>
                    <InputGroup>
                      <InputLeftElement pointerEvents="none">
                        <CurrencyRupeeIcon className="h-5 w-5 text-gray-400" />
                      </InputLeftElement>
                      <Input
                        {...register("salary_max_amount", {
                          required: "Maximum salary is required",
                          pattern: { value: /^[0-9]+$/, message: "Enter a valid number" },
                          validate: (value) => {
                            const minSalary = watch("salary_min_amount");
                            return !minSalary || Number(value) > Number(minSalary) || "Must be greater than minimum";
                          },
                        })}
                        pl={10}
                        placeholder="e.g., 70000"
                      />
                    </InputGroup>
                  </FormControl>

                  <FormControl isRequired isInvalid={errors.salary_per_type}>
                    <FormLabel>Salary Period</FormLabel>
                    <Select
                      {...register("salary_per_type", { required: "Please select a salary period" })}
                      defaultValue="Monthly"
                    >
                      <option value="Weekly">Weekly</option>
                      <option value="Monthly">Monthly</option>
                      <option value="Yearly">Yearly</option>
                    </Select>
                  </FormControl>
                </SimpleGrid>
              )}

              <FormControl mt={4}>
                <FormLabel>Additional Benefits</FormLabel>
                <HStack flexWrap="wrap" spacing={2} mb={2}>
                  {benefits.length > 0 ? (
                    benefits.map((b, idx) => (
                      <Tag key={`${b}-${idx}`} size="md" borderRadius="full" variant="solid" colorScheme="blue">
                        <TagLabel>{b}</TagLabel>
                        <TagCloseButton onClick={() => removeBenefit(idx)} />
                      </Tag>
                    ))
                  ) : (
                    <Text fontSize="sm" color="gray.500">
                      Add one or more benefits
                    </Text>
                  )}
                </HStack>
                <Select
                  placeholder="Select a benefit to add"
                  onChange={(e) => {
                    addBenefit(e.target.value);
                    e.target.value = "";
                  }}
                >
                  {benefitOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </Select>
                <Text fontSize="xs" color={packedBenefits.text.length >= ADDITIONAL_PAY_MAX_CHARS ? "red.500" : "gray.500"} mt={1}>
                  Characters: {packedBenefits.text.length}/{ADDITIONAL_PAY_MAX_CHARS}
                </Text>
              </FormControl>

              <FormControl mt={4}>
                <FormLabel>Is there any joining amount? (Fees)</FormLabel>
                <RadioGroup
                  onChange={(val) => {
                    const req = val === "true";
                    setValue("joining_amount_required", req, { shouldValidate: true });
                    if (!req) setValue("joining_amount", "", { shouldValidate: true });
                  }}
                  value={watch("joining_amount_required") ? "true" : "false"}
                >
                  <Stack direction="row">
                    <Radio value="true">Yes</Radio>
                    <Radio value="false">No</Radio>
                  </Stack>
                </RadioGroup>
              </FormControl>

              {watch("joining_amount_required") && (
                <FormControl mt={2} isInvalid={errors.joining_amount}>
                  <FormLabel>Joining Amount</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">₹</InputLeftElement>
                    <Input
                      placeholder="Enter amount"
                      {...register("joining_amount", {
                        required: "Joining amount is required",
                        pattern: { value: /^[0-9]+$/, message: "Enter a valid number" },
                      })}
                      type="number"
                      min="0"
                      pl={8}
                    />
                  </InputGroup>
                </FormControl>
              )}

              {/* Description */}
              <Box p={4} borderLeft="4px solid" borderColor={accentColor} bg="blue.50" borderRadius="md" mt={6}>
                <Heading as="h3" size="md" mb={2}>
                  Job Description
                </Heading>
                <Text color="gray.600">Create a compelling job description.</Text>
              </Box>

              <FormControl isRequired isInvalid={errors.job_description} mt={4}>
                <FormLabel>Job Description</FormLabel>
                <Box border="1px solid" borderColor={errors.job_description ? "red.500" : "gray.200"} borderRadius="md">
                  <ReactQuill
                    theme="snow"
                    modules={modules}
                    formats={formats}
                    placeholder="Describe responsibilities, qualifications, benefits..."
                    style={{ height: "250px", marginBottom: "50px" }}
                    value={quillContent}
                    onChange={handleQuillChange}
                  />
                </Box>

                <input
                  type="hidden"
                  {...register("job_description", {
                    required: "Job description is required",
                    validate: (value) => {
                      const plainText = stripHtml(value || "");
                      if (plainText.trim().length < 50) return "Job description must be at least 50 characters";
                      return true;
                    },
                  })}
                />
              </FormControl>

              {/* Planned start date */}
              <FormControl display="flex" alignItems="center" mt={4}>
                <FormLabel mb="0" mr={4}>
                  Is there a specific date for the candidate to join?
                </FormLabel>
                <input type="checkbox" hidden {...register("is_planned_start_date")} />
                <Switch
                  colorScheme="blue"
                  size="lg"
                  isChecked={isPlannedStart}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    setIsPlannedStart(isChecked);
                    setValue("is_planned_start_date", isChecked, { shouldValidate: true });
                    if (!isChecked) setValue("plan_start_date", null);
                    else setTimeout(() => trigger("plan_start_date"), 0);
                  }}
                />
              </FormControl>

              {isPlannedStart && (
                <FormControl isRequired={isPlannedStart} isInvalid={errors.plan_start_date} mt={2}>
                  <FormLabel>Joining date</FormLabel>
                  <Input
                    type="date"
                    {...register("plan_start_date", {
                      required: isPlannedStart ? "Joining date is required" : false,
                      validate: (value) => {
                        if (!isPlannedStart) return true;
                        if (!value) return "Joining date is required";
                        const selectedDate = new Date(value);
                        const todayD = new Date();
                        todayD.setHours(0, 0, 0, 0);
                        return selectedDate >= todayD || "Joining date must be today or in the future";
                      },
                    })}
                    onChange={(e) => {
                      const d = new Date(e.target.value);
                      if (!isNaN(d.getTime())) {
                        const formatted = d.toISOString().split("T")[0];
                        setValue("plan_start_date", formatted, { shouldValidate: true });
                      }
                    }}
                  />
                </FormControl>
              )}

              <FormControl mt={4}>
                <FormLabel>Number of Openings</FormLabel>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <UserGroupIcon className="h-5 w-5 text-gray-400" />
                  </InputLeftElement>
                  <Input
                    {...register("no_of_hire", {
                      pattern: { value: /^[0-9]+$/, message: "Enter a valid number" },
                      min: { value: 1, message: "At least 1" },
                      max: { value: 999, message: "Cannot exceed 999" },
                    })}
                    placeholder="e.g., 10"
                    pl={10}
                  />
                </InputGroup>
              </FormControl>

              {/* Additional Job Details */}
              <Divider my={6} />
              <Heading as="h3" size="md" mb={2} color="blue.700">
                Additional Job Details
              </Heading>

              <Box p={5} borderWidth="1px" borderRadius="lg" borderColor="blue.100" bg="blue.50">
                <FormControl>
                  <FormLabel display="flex" alignItems="center">
                    Upload CV?
                    <Tooltip label="Choose whether applicants must upload their CV">
                      <Box as="span" ml={1}>
                        <InformationCircleIcon className="h-4 w-4 text-gray-500" />
                      </Box>
                    </Tooltip>
                  </FormLabel>
                  <RadioGroup
                    onChange={(value) => setValue("upload_cv", value === "true", { shouldValidate: true })}
                    value={watch("upload_cv") ? "true" : "false"}
                  >
                    <Stack direction="row">
                      <Radio value="true">Yes</Radio>
                      <Radio value="false">No</Radio>
                    </Stack>
                  </RadioGroup>
                </FormControl>
              </Box>

              <Divider my={4} />

              <Heading as="h3" size="sm" mb={2} color="blue.700">
                Location and Demographic Requirements
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                <FormControl isInvalid={formSubmitted && !!locationError}>
                  <FormLabel>Job Posting Location</FormLabel>
                  <Menu closeOnSelect={false} autoSelect={false}>
                    <MenuButton
                      as={Button}
                      variant="outline"
                      rightIcon={<ChevronDownIcon className="h-4 w-4" />}
                      width="100%"
                      justifyContent="space-between"
                      fontWeight="normal"
                      borderColor={formSubmitted && locationError ? "red.500" : "gray.200"}
                    >
                      Add job locations
                    </MenuButton>
                    <MenuList p={3} minW={{ base: "100%", md: "sm" }}>
                      <Input
                        size="sm"
                        placeholder="Type to search locations..."
                        value={locationSearch}
                        onChange={(e) => setLocationSearch(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                        autoFocus
                      />
                      <Box maxH="260px" overflowY="auto" mt={2}>
                        {coreLocationLoading ? (
                          <HStack p={3}>
                            <Spinner size="sm" />
                            <Text>Searching...</Text>
                          </HStack>
                        ) : coreLocation && coreLocation.length > 0 ? (
                          coreLocation.map((item) => {
                            const already = location.some((loc) => loc.uuid === item.uuid);
                            return (
                              <MenuItem key={item.uuid} onClick={() => handleSelectLocationItem(item)} isDisabled={already}>
                                <HStack w="100%" alignItems="center">
                                  <Text>{item.title}</Text>
                                  {already && (
                                    <Text ml="auto" fontSize="xs" color="red.500">
                                      Added
                                    </Text>
                                  )}
                                </HStack>
                              </MenuItem>
                            );
                          })
                        ) : (
                          <Text p={3} color="gray.500">
                            No results
                          </Text>
                        )}
                      </Box>
                    </MenuList>
                  </Menu>
                  {location.length > 0 ? (
                    <HStack mt={2} flexWrap="wrap" spacing={2}>
                      {location.map((loc, index) => (
                        <Tag key={`${loc.uuid}-${index}`} size="md" borderRadius="full" variant="solid" colorScheme="blue" mt={1}>
                          <TagLabel>{loc.title}</TagLabel>
                          <TagCloseButton onClick={() => removeLocation(index)} />
                        </Tag>
                      ))}
                    </HStack>
                  ) : (
                    <Text fontSize="sm" color="gray.500" mt={1}>
                      Add one or more job locations
                    </Text>
                  )}
                  {formSubmitted && locationError && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {locationError}
                    </Text>
                  )}
                </FormControl>

                <FormControl isRequired isInvalid={errors.job_apply_age}>
                  <FormLabel>Age Range</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <UserIcon className="h-5 w-5 text-gray-400" />
                    </InputLeftElement>
                    <Select
                      {...register("job_apply_age", { required: "Age range is required" })}
                      defaultValue="18-25"
                      pl={10}
                      bg="white"
                      cursor="pointer"
                    >
                      <option value="18-25">18-25 years</option>
                      <option value="26-30">26-30 years</option>
                      <option value="31-35">31-35 years</option>
                      <option value="36-40">36-40 years</option>
                      <option value="45+">45+ years</option>
                    </Select>
                  </InputGroup>
                </FormControl>

                <FormControl isRequired isInvalid={errors.candidate_gender}>
                  <FormLabel>Gender</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <UserIcon className="h-5 w-5 text-gray-400" />
                    </InputLeftElement>
                    <Select
                      {...register("candidate_gender", { required: "Gender is required" })}
                      defaultValue="Both"
                      pl={10}
                      bg="white"
                      cursor="pointer"
                    >
                      <option value="Both">Both</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </Select>
                  </InputGroup>
                </FormControl>
              </SimpleGrid>

              <Divider my={4} />

              <Heading as="h3" size="sm" mb={2} color="blue.700">
                Skills and Qualifications
              </Heading>
              <VStack spacing={6} align="stretch">
                <FormControl isInvalid={formSubmitted && !!skillsError}>
                  <FormLabel>Skills Required</FormLabel>
                  <Menu closeOnSelect={false} autoSelect={false}>
                    <MenuButton
                      as={Button}
                      variant="outline"
                      rightIcon={<ChevronDownIcon className="h-4 w-4" />}
                      width="100%"
                      justifyContent="space-between"
                      fontWeight="normal"
                      borderColor={formSubmitted && skillsError ? "red.500" : "gray.200"}
                    >
                      Add required skills
                    </MenuButton>
                    <MenuList p={3} minW={{ base: "100%", md: "sm" }}>
                      <Input
                        size="sm"
                        placeholder="Type to search skills..."
                        value={skillSearch}
                        onChange={(e) => setSkillSearch(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                        autoFocus
                      />
                      <Box maxH="260px" overflowY="auto" mt={2}>
                        {isSuggestLoading ? (
                          <HStack p={3}>
                            <Spinner size="sm" />
                            <Text>Searching...</Text>
                          </HStack>
                        ) : skillsListToShow && skillsListToShow.length > 0 ? (
                          skillsListToShow.map((item) => {
                            const already = skills.some((s) => s.uuid === item.uuid);
                            return (
                              <MenuItem key={item.uuid} onClick={() => handleSelectSkillItem(item)} isDisabled={already}>
                                {item.title}
                                {already && (
                                  <Text ml="auto" fontSize="xs" color="red.500">
                                    Added
                                  </Text>
                                )}
                              </MenuItem>
                            );
                          })
                        ) : (
                          <Text p={3} color="gray.500">
                            No results
                          </Text>
                        )}
                      </Box>
                    </MenuList>
                  </Menu>

                  {skills.length > 0 ? (
                    <HStack mt={2} flexWrap="wrap" spacing={2}>
                      {skills.map((skill, index) => (
                        <Tag key={`${skill.uuid}-${index}`} size="md" borderRadius="full" variant="solid" colorScheme="green" mt={1}>
                          <TagLabel>{skill.title}</TagLabel>
                          <TagCloseButton onClick={() => removeSkill(index)} />
                        </Tag>
                      ))}
                    </HStack>
                  ) : (
                    <Text fontSize="sm" color="gray.500" mt={1}>
                      Add one or more required skills
                    </Text>
                  )}
                  {formSubmitted && skillsError && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {skillsError}
                    </Text>
                  )}
                </FormControl>

                {/* Education Requirements - Hierarchical: Level → Course → Speciality */}
                <FormControl isInvalid={formSubmitted && !!educationError}>
                  <FormLabel>Education Requirements</FormLabel>

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                    {/* Education Level (required to add) */}
                    <Select
                      placeholder="Select education level"
                      value={selectedEduLevel?.uuid ?? ""}
                      onChange={(e) => {
                        const lvl = levels.find((l) => l.uuid === e.target.value);
                        setSelectedEduLevel(lvl || null);
                        resetCourseAndBelow();
                      }}
                      bg="white"
                      cursor="pointer"
                    >
                      {levels.map((lvl) => (
                        <option key={lvl.uuid} value={lvl.uuid}>
                          {lvl.title}
                        </option>
                      ))}
                    </Select>

                    {/* Course (optional) */}
                    <Select
                      placeholder={courses.length ? "Select course (optional)" : "No courses"}
                      value={selectedEduCourse?.uuid ?? ""}
                      onChange={(e) => {
                        const crs = courses.find((c) => c.uuid === e.target.value);
                        setSelectedEduCourse(crs || null);
                        resetSpeciality();
                      }}
                      isDisabled={!selectedEduLevel || courses.length === 0}
                      bg="white"
                      cursor={courses.length ? "pointer" : "not-allowed"}
                    >
                      {courses.map((crs) => (
                        <option key={crs.uuid} value={crs.uuid}>
                          {crs.title}
                        </option>
                      ))}
                    </Select>

                    {/* Speciality (optional) */}
                    <Select
                      placeholder={specialities.length ? "Select speciality (optional)" : "No specialities"}
                      value={selectedEduSpeciality?.uuid ?? ""}
                      onChange={(e) => {
                        const spec = specialities.find((s) => s.uuid === e.target.value);
                        setSelectedEduSpeciality(spec || null);
                      }}
                      isDisabled={!selectedEduCourse || specialities.length === 0}
                      bg="white"
                      cursor={specialities.length ? "pointer" : "not-allowed"}
                    >
                      {specialities.map((spec) => (
                        <option key={spec.uuid} value={spec.uuid}>
                          {spec.title}
                        </option>
                      ))}
                    </Select>
                  </SimpleGrid>

                  <HStack mt={3} spacing={3}>
                    <Button colorScheme="blue" onClick={addSelectedEducation} isDisabled={!selectedEduLevel}>
                      Add
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setSelectedEduLevel(null);
                        resetCourseAndBelow();
                      }}
                    >
                      Clear selection
                    </Button>
                  </HStack>

                  {education.length > 0 ? (
                    <HStack mt={3} flexWrap="wrap" spacing={2}>
                      {education.map((edu, index) => (
                        <Tag key={`${edu.uuid}-${index}`} size="md" borderRadius="full" variant="solid" colorScheme="purple" mt={1}>
                          <TagLabel>{edu.title}</TagLabel>
                          <TagCloseButton onClick={() => removeEducation(index)} />
                        </Tag>
                      ))}
                    </HStack>
                  ) : (
                    <Text fontSize="sm" color="gray.500" mt={2}>
                      Add at least one education requirement
                    </Text>
                  )}

                  {formSubmitted && educationError && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {educationError}
                    </Text>
                  )}
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Experience Required</FormLabel>
                  <Select
                    onChange={(e) => {
                      const isFresher = e.target.value === "fresher";
                      if (isFresher) {
                        setValue("min_job_exp", "0", { shouldValidate: true });
                        setValue("max_job_exp", "0", { shouldValidate: true });
                      } else if (watch("min_job_exp") === "0" && watch("max_job_exp") === "0") {
                        setValue("min_job_exp", "1", { shouldValidate: true });
                        setValue("max_job_exp", "3", { shouldValidate: true });
                      }
                    }}
                    mb={4}
                    bg="white"
                    defaultValue={watchMinExp === "0" && watchMaxExp === "0" ? "fresher" : "experienced"}
                  >
                    <option value="fresher">Fresher</option>
                    <option value="experienced">Experienced</option>
                  </Select>

                  {watch("min_job_exp") === "0" && watch("max_job_exp") === "0" ? (
                    <Text fontSize="sm" color="blue.500" mb={2}>
                      No experience required (Fresher position)
                    </Text>
                  ) : (
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <FormControl isRequired isInvalid={errors.min_job_exp}>
                        <FormLabel fontSize="sm">Minimum Experience</FormLabel>
                        <InputGroup>
                          <InputLeftElement pointerEvents="none">
                            <BriefcaseIcon className="h-5 w-5 text-gray-400" />
                          </InputLeftElement>
                          <Select
                            {...register("min_job_exp", { required: "Minimum experience is required" })}
                            pl={10}
                            defaultValue={job?.min_job_exp?.toString() || "1"}
                            bg="white"
                          >
                            <option value="0">0 year</option>
                            <option value="1">1 year</option>
                            <option value="2">2 years</option>
                            <option value="3">3 years</option>
                            <option value="4">4 years</option>
                            <option value="5">5 years</option>
                          </Select>
                        </InputGroup>
                      </FormControl>
                      <FormControl isRequired isInvalid={errors.max_job_exp}>
                        <FormLabel fontSize="sm">Maximum Experience</FormLabel>
                        <InputGroup>
                          <InputLeftElement pointerEvents="none">
                            <BriefcaseIcon className="h-5 w-5 text-gray-400" />
                          </InputLeftElement>
                          <Select
                            {...register("max_job_exp", {
                              required: "Maximum experience is required",
                              validate: (value) => {
                                const minExp = Number(watchMinExp);
                                const maxExp = Number(value);
                                return maxExp >= minExp || "Maximum must be >= minimum";
                              },
                            })}
                            pl={10}
                            defaultValue={job?.max_job_exp?.toString() || "3"}
                            bg="white"
                          >
                            <option value="1">1 year</option>
                            <option value="2">2 years</option>
                            <option value="3">3 years</option>
                            <option value="4">4 years</option>
                            <option value="5">5 years</option>
                            <option value="6">6 years</option>
                            <option value="7">7 years</option>
                            <option value="8">8 years</option>
                            <option value="9">9 years</option>
                            <option value="10">10 years</option>
                            <option value="11">11+ years</option>
                          </Select>
                        </InputGroup>
                      </FormControl>
                    </SimpleGrid>
                  )}
                </FormControl>
              </VStack>

              <Divider my={4} />

              <Heading as="h3" size="sm" mb={2} color="blue.700">
                Application Process
              </Heading>
              <VStack spacing={6} align="stretch">
                <FormControl isRequired isInvalid={errors.interview_type}>
                  <FormLabel>Interview Type</FormLabel>
                  <RadioGroup
                    onChange={(val) => setValue("interview_type", val, { shouldValidate: true })}
                    value={watch("interview_type") || "In-Person"}
                  >
                    <Stack direction="column" spacing={1} alignItems={"start"}>
                      <Radio value="In-Person" alignItems={"start"}>
                        <HStack alignItems="center">
                          <Text>In-Person / Walk-in Interview</Text>
                          <Tag size="sm" colorScheme="orange" ml={2}>
                            Face-to-face
                          </Tag>
                        </HStack>
                      </Radio>
                      <Radio value="Online" alignItems={"start"}>
                        <HStack alignItems="start">
                          <Text>Telephonic / Online Interview</Text>
                          <Tag size="sm" colorScheme="blue" ml={2}>
                            Remote
                          </Tag>
                        </HStack>
                      </Radio>
                    </Stack>
                  </RadioGroup>
                </FormControl>

                <FormControl isRequired isInvalid={errors.candidate_contact_via}>
                  <FormLabel>Candidate Contact Preference</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <PhoneIcon className="h-5 w-5 text-gray-400" />
                    </InputLeftElement>
                    <Select
                      {...register("candidate_contact_via", { required: "Contact preference is required" })}
                      pl={10}
                      defaultValue="By call"
                      bg="white"
                    >
                      <option value="By call">By call</option>
                      <option value="By email">By email</option>
                      <option value="By WhatsApp">By WhatsApp</option>
                      <option value="All modes">All modes</option>
                    </Select>
                  </InputGroup>
                </FormControl>

                <FormControl isRequired isInvalid={errors.job_deadline}>
                  <FormLabel display="flex" alignItems="center">
                    Last Date to apply
                    <Tooltip label="Last date when candidates can apply for this position">
                      <Box as="span" ml={1}>
                        <InformationCircleIcon className="h-4 w-4 text-gray-500" />
                      </Box>
                    </Tooltip>
                  </FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <CalendarIcon className="h-5 w-5 text-gray-400" />
                    </InputLeftElement>
                    <Input
                      type="date"
                      {...register("job_deadline", {
                        required: "Application deadline is required",
                        validate: (value) => {
                          const selectedDate = new Date(value);
                          const todayD = new Date();
                          todayD.setHours(0, 0, 0, 0);
                          return selectedDate >= todayD || "Deadline must be today or in the future";
                        },
                      })}
                      pl={10}
                      min={today}
                      bg="white"
                    />
                  </InputGroup>
                  <Text fontSize="sm" color="gray.500" mt={1}>
                    Job will be automatically closed after this date
                  </Text>
                </FormControl>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button mr={3} onClick={onClose2}>
                Close
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleSubmit(handleUpdate, onInvalid)}
                isLoading={isSubmitting}
                loadingText="Saving..."
              >
                Save Changes
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default JobReview2;